/* eslint-disable react-refresh/only-export-components */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { fetchCompanyProfile } from '@/api/companies';
import { RiskProfileTab } from '@/app/(client)/network/-components/companyProfileTabs/riskProfile/RiskProfileTab';
import { useCompanyDetail } from '@/app/(client)/network/-hooks/useCompanyDetail';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

const searchSchema = z.object({
  blueprint: z.string().optional(),
});

function isFeatureEnabled(
  features: Array<{ id: string }> | undefined,
  id: string,
): boolean {
  return features?.some(f => f.id === id) ?? false;
}

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId/risk-profile',
)({
  validateSearch: searchSchema,
  beforeLoad: async ({ params }) => {
    const company = await queryClient.ensureQueryData({
      queryKey: ['network', 'company', params.companyId],
      queryFn: () => fetchCompanyProfile(params.companyId),
    });
    const user = useSessionStore.getState().user;
    const isOwnCompany =
      user?.companyId === (company?.forCompanyId ?? params.companyId);
    const allowed =
      isFeatureEnabled(user?.company?.enabledFeatures, 'risk-analysis-v3') ||
      isOwnCompany;
    if (!allowed) {
      throw redirect({
        to: '/network/$companyId/general-info',
        params: { companyId: params.companyId },
      });
    }
  },
  component: RiskProfileRoute,
});

function RiskProfileRoute() {
  const { companyId } = Route.useParams();
  const { data: company } = useCompanyDetail(companyId);
  const user = useSessionStore(s => s.user);
  return (
    <RiskProfileTab
      companyId={companyId}
      company={company}
      isRiskAnalysisUser={isFeatureEnabled(
        user?.company?.enabledFeatures,
        'risk-analysis-v3',
      )}
    />
  );
}
